import React, {forwardRef, KeyboardEvent, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react'

import EntitySelectorGlist from "../glist/glist"
import EntitySelectorItem from "../glist/item"
import {useEntitySelectorContext} from "../context"
import {MentionOption} from "../../editor/plugins/mentions/option"
import {SortDirection} from "../../../data-structures/sort"

type Props = {
    search              : string

    titleLabel          : string    // the list's first column header
    dateLabel           : string    // the list's date column header
    noItemsMessage      : string
    dateSortDirection?  : SortDirection

    // Everything a tab differs in. It takes the search string because the same
    // list shows both the default rows and the search results.
    loadItems           : {(search: string): Promise<EntitySelectorItem[]>}
}

// const debug = "EntitySelectorListTab"
const debug = false

// Every flat-list tab -- Task/Recent, Document, Meeting. It owns the loading and
// the pick; the rows and their look belong to the shared glist, so the three
// tabs cannot drift apart. A tab that browses a tree (Task/Browse, Project) is
// not one of these.
const EntitySelectorListTab = forwardRef((props: Props, ref) => {
    const context = useEntitySelectorContext()

    debug && console.log(debug, 'render', props.titleLabel, props.search)

    const [items, setItems]         = useState<EntitySelectorItem[]>([])
    const [isLoading, setIsLoading] = useState(true)

    const glistRef                  = useRef<any>()

    // A stale response must not overwrite a newer one: the search string changes
    // faster than the requests come back.
    const requestIdRef              = useRef(0)

    useEffect(() => {
        const requestId = ++requestIdRef.current

        setIsLoading(true)

        props.loadItems(props.search).then((loaded: EntitySelectorItem[]) => {
            if (requestId !== requestIdRef.current) {
                return
            }

            setItems(loaded)
            setIsLoading(false)
        }).catch((error: any) => {
            debug && console.log(debug, 'load failed', error)

            if (requestId !== requestIdRef.current) {
                return
            }

            // An empty list, never a spinner that never stops.
            setItems([])
            setIsLoading(false)
        })

        // `loadItems` is in here so a change of SCOPE reloads too (a tab that
        // rebinds it -- to a project, a date window -- gets a fresh load). Every
        // caller passes a module-level function or a useCallback, so this cannot
        // loop.
    }, [props.search, props.loadItems])

    const onSelect = useCallback((item: EntitySelectorItem) => {
        const option = new MentionOption({
            id  : item.id,
            name: item.title,
            type: item.entity.itemType,
            data: item.entity
        })

        debug && console.log(debug, 'onSelect', option)

        context.onEntitySelected(option)
    }, [context])

    const onKeyDown = useCallback((event: KeyboardEvent) => {
        glistRef.current?.onKeyDown(event)
    }, [])

    useImperativeHandle(ref, () => ({
        onKeyDown,
    }))

    return (
        <EntitySelectorGlist
            ref                 = {glistRef}
            items               = {items}
            isLoading           = {isLoading}
            titleLabel          = {props.titleLabel}
            dateLabel           = {props.dateLabel}
            noItemsMessage      = {props.noItemsMessage}
            dateSortDirection   = {props.dateSortDirection}
            onSelect            = {onSelect}
        />
    )
})

export default EntitySelectorListTab
